import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import moment from "moment";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import {
  addPrivateNoteApi,
  deletePrivateNoteApi,
  listSubmissionPrivateNotesApi,
  updateSubmissionPrivateNoteApi,
} from "src/DAL/tracker/trackerSubmission";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";
// Sticky-note palette — yellow is the usual cue for "internal / private".
const NOTE_BG = "#fff8d6";
const NOTE_BORDER = "#f1e2a4";
const NOTE_INK = "#3f3a1e";
const NOTE_MUTED = "#8a7b45";
export const PRIVATE_NOTE_MAX = 5000;
// Longer than this (or more lines) starts collapsed with "See more".
const CLAMP_CHARS = 180;
const CLAMP_LINES = 4;

/** "just now", "14m ago", "3h ago", "2d ago", then "12 Oct" / "12 Oct 2025". */
const shortAgo = (value) => {
  const at = moment(value);
  if (!at.isValid()) return "";
  const minutes = moment().diff(at, "minutes");
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = moment().diff(at, "hours");
  if (hours < 24) return `${hours}h ago`;
  const days = moment().diff(at, "days");
  if (days < 7) return `${days}d ago`;
  return at.format(at.isSame(moment(), "year") ? "D MMM" : "D MMM YYYY");
};

const textLinkSx = {
  p: 0,
  border: 0,
  background: "none",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 700,
};

/**
 * One-line input that grows as you type, with a small add button.
 * Enter adds, Shift+Enter makes a new line. `onAdd(text)` → true when saved.
 */
export function PrivateNoteComposer({ onAdd, placeholder }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const empty = !draft.trim();

  const submit = async () => {
    if (empty || saving) return;
    setSaving(true);
    const ok = await onAdd(draft.trim());
    setSaving(false);
    if (ok) setDraft("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 0.5,
        pl: 1.25,
        pr: 0.5,
        py: 0.5,
        borderRadius: "10px",
        border: `1px solid ${BORDER}`,
        bgcolor: "#fff",
        "&:focus-within": { borderColor: PRIMARY },
      }}
    >
      <InputBase
        fullWidth
        multiline
        maxRows={5}
        placeholder={placeholder || "Write a private note"}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, PRIVATE_NOTE_MAX))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        sx={{ fontSize: 13, py: 0.35 }}
      />
      <Tooltip title="Add note (Enter)">
        <span>
          <IconButton
            size="small"
            aria-label="Add note"
            onClick={submit}
            disabled={empty || saving}
            sx={{
              width: 28,
              height: 28,
              bgcolor: PRIMARY,
              color: "#fff",
              "&:hover": { bgcolor: PRIMARY, opacity: 0.9 },
              "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            {saving ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : (
              <ArrowUpwardIcon sx={{ fontSize: 17 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

/** A single sticky note: text first, then time + edit / delete. */
function StickyNote({ note, context, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text || "");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const text = String(note.text || "");
  const isLong =
    text.length > CLAMP_CHARS || text.split("\n").length > CLAMP_LINES;

  const save = async () => {
    const next = draft.trim();
    if (!next || saving) return;
    if (next === note.text) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onEdit(note, next);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <Box
      sx={{
        position: "relative",
        minWidth: 0,
        px: 1.25,
        pt: 1,
        pb: 0.75,
        borderRadius: "8px",
        bgcolor: NOTE_BG,
        border: `1px solid ${NOTE_BORDER}`,
        boxShadow: "0 1px 2px rgba(120, 100, 20, 0.08)",
        "& .note-actions": { opacity: 0.55, transition: "opacity .15s" },
        "&:hover .note-actions": { opacity: 1 },
      }}
    >
      {context ? <Box sx={{ mb: 0.5 }}>{context}</Box> : null}

      {editing ? (
        <>
          <InputBase
            fullWidth
            multiline
            autoFocus
            maxRows={10}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, PRIVATE_NOTE_MAX))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                save();
              }
              if (e.key === "Escape") setEditing(false);
            }}
            sx={{
              fontSize: 13,
              lineHeight: 1.5,
              color: NOTE_INK,
              bgcolor: "rgba(255,255,255,0.7)",
              borderRadius: "6px",
              px: 0.75,
              py: 0.25,
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 0.75 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              sx={{ ...textLinkSx, color: NOTE_MUTED }}
            >
              Cancel
            </Box>
            <Box
              component="button"
              type="button"
              onClick={save}
              disabled={saving || !draft.trim()}
              sx={{ ...textLinkSx, color: PRIMARY }}
            >
              {saving ? "Saving…" : "Save"}
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.5,
              color: NOTE_INK,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              ...(isLong && !expanded
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: CLAMP_LINES,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : null),
            }}
          >
            {text}
          </Typography>
          {isLong ? (
            <Box
              component="button"
              type="button"
              onClick={() => setExpanded((open) => !open)}
              sx={{ ...textLinkSx, mt: 0.25, color: PRIMARY }}
            >
              {expanded ? "See less" : "See more"}
            </Box>
          ) : null}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.5 }}>
            <Tooltip
              title={moment(note.createdAt).format("D MMM YYYY, h:mm A")}
              placement="bottom-start"
            >
              <Typography sx={{ fontSize: 11, color: NOTE_MUTED }}>
                {shortAgo(note.createdAt)}
                {note.updatedAt ? " · edited" : ""}
              </Typography>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Box className="note-actions" sx={{ display: "flex" }}>
              <IconButton
                size="small"
                aria-label="Edit note"
                onClick={() => {
                  setDraft(text);
                  setEditing(true);
                }}
                sx={{ p: 0.3, color: NOTE_MUTED, "&:hover": { color: PRIMARY } }}
              >
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Delete note"
                onClick={() => onDelete(note)}
                sx={{ p: 0.3, color: NOTE_MUTED, "&:hover": { color: "#dc2626" } }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * Notes as sticky notes. `layout="stack"` for narrow columns, `"grid"` for
 * wide areas (Keep-style tiles). `getContext(note)` adds a line on top.
 */
export function PrivateNotesList({
  notes,
  layout = "stack",
  getContext,
  onEdit,
  onDelete,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1,
        gridTemplateColumns:
          layout === "grid"
            ? "repeat(auto-fill, minmax(220px, 1fr))"
            : "minmax(0, 1fr)",
        alignItems: "start",
      }}
    >
      {notes.map((note) => (
        <StickyNote
          key={note.key || note._id}
          note={note}
          context={getContext ? getContext(note) : null}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </Box>
  );
}

/** Small uppercase header, same look as the other side cards. */
export function PrivateNotesHeader({ count, action = null }) {
  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.35,
        borderBottom: `1px solid ${BORDER}`,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 14, color: MUTED }} />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.45,
          color: MUTED,
          textTransform: "uppercase",
        }}
      >
        Private notes{count ? ` (${count})` : ""}
      </Typography>
      <Tooltip title="Only you can see your notes">
        <Typography sx={{ fontSize: 11, color: "#94a3b8", cursor: "default" }}>
          · only you
        </Typography>
      </Tooltip>
      {action ? (
        <>
          <Box sx={{ flex: 1 }} />
          {action}
        </>
      ) : null}
    </Box>
  );
}

/** Card frame shared by the submission page and the Overview. */
export const notesCardSx = {
  border: `1px solid ${BORDER}`,
  borderRadius: "12px",
  bgcolor: "#fff",
  overflow: "hidden",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

/**
 * The signed-in coach's / admin's own private notes on a submission.
 * Nobody else sees them (not other staff, never the member).
 */
export default function PrivateCoachNotesCard({ submission }) {
  const { enqueueSnackbar } = useSnackbar();
  const submissionId = submission?.id || submission?._id;
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteNote, setDeleteNote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!submissionId) return;
      setLoading(true);
      try {
        const res = await listSubmissionPrivateNotesApi(submissionId);
        if (!cancelled) setNotes(res?.code === 200 ? res.notes || [] : []);
      } catch (_err) {
        if (!cancelled) setNotes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  /** Run a notes API call; on success keep the returned list. */
  const run = async (call, okMessage, failMessage) => {
    try {
      const res = await call();
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || failMessage, { variant: "error" });
        return false;
      }
      setNotes(res.notes || []);
      enqueueSnackbar(okMessage, { variant: "success" });
      return true;
    } catch (_err) {
      enqueueSnackbar(failMessage, { variant: "error" });
      return false;
    }
  };

  const handleDelete = async () => {
    const note = deleteNote;
    setDeleteNote(null);
    if (!note?._id) return;
    await run(
      () => deletePrivateNoteApi(submissionId, note._id),
      "Note deleted",
      "Could not delete the note"
    );
  };

  return (
    <Box sx={notesCardSx}>
      <PrivateNotesHeader count={notes.length} />
      <Box sx={{ px: 1.75, py: 1.5 }}>
        <PrivateNoteComposer
          onAdd={(text) =>
            run(
              () => addPrivateNoteApi(submissionId, text),
              "Note added",
              "Could not add the note"
            )
          }
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : notes.length ? (
          <Box sx={{ mt: 1.25 }}>
            <PrivateNotesList
              notes={notes}
              onEdit={(note, text) =>
                run(
                  () => updateSubmissionPrivateNoteApi(submissionId, note._id, text),
                  "Note updated",
                  "Could not update the note"
                )
              }
              onDelete={setDeleteNote}
            />
          </Box>
        ) : null}
      </Box>

      <CustomConfirmation
        openDelete={Boolean(deleteNote)}
        setOpenDelete={(open) => {
          if (!open) setDeleteNote(null);
        }}
        title="Delete this note?"
        handleAgree={handleDelete}
      />
    </Box>
  );
}
